import { Box } from '@mui/material';
import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { AiChatConversation } from '../component/aichat/AiChatPanel';
import { useIsMobileShell } from '../hooks/useIsMobileShell';
import { isAiChatEnabled } from '../services/aiChat';

/**
 * Página do Assistente de IA em tela cheia (`/chat`), acessada pelo menu lateral.
 * Reaproveita `AiChatConversation` (a mesma usada no painel lateral da topbar).
 * A conversa daqui é independente da do painel; o histórico (servidor) é comum.
 */
export default function AiChat() {
  const habilitado = isAiChatEnabled();
  const isMobileShell = useIsMobileShell();

  // A página ocupa exatamente o viewport: só a lista de mensagens rola e o
  // campo de pergunta fica fixo embaixo. Trava o scroll do documento aqui
  // (restaurado ao sair) para o campo nunca rolar junto com a página.
  useEffect(() => {
    if (!habilitado) return undefined;
    const html = document.documentElement;
    const anterior = html.style.overflow;
    html.style.overflow = 'hidden';
    return () => { html.style.overflow = anterior; };
  }, [habilitado]);

  if (!habilitado) return <Navigate to="/home" replace />;

  // Shell mobile (MobileLayout): ocupa a área abaixo da barra superior (61px + safe-area),
  // anulando o padding do <main> (16px; 28px + safe-area embaixo, barra inferior oculta em /chat).
  const alturaMobile = 'calc(100vh - 61px - env(safe-area-inset-top, 0px))';
  const alturaMobileDvh = 'calc(100dvh - 61px - env(safe-area-inset-top, 0px))';

  return (
    <Box
      data-testid="ai-chat-page"
      sx={isMobileShell ? {
        display: 'flex',
        flexDirection: 'column',
        flex: '0 0 auto',
        minHeight: 0,
        height: alturaMobile,
        maxHeight: alturaMobile,
        '@supports (height: 100dvh)': { height: alturaMobileDvh, maxHeight: alturaMobileDvh },
        margin: '-16px -16px calc(-28px - env(safe-area-inset-bottom, 0px))',
        backgroundColor: 'background.default',
        overflow: 'hidden',
      } : {
        display: 'flex',
        flexDirection: 'column',
        // `flex: 0 0 auto`: com `flex: 1` o pai (main, flex em coluna) sobrescreve
        // `height` via flex-basis e o card crescia junto com as mensagens.
        flex: '0 0 auto',
        minHeight: 0,
        // Topbar 68px + padding vertical do <main> (Main.js). `dvh` evita o
        // estouro causado pela barra de endereço em navegadores móveis.
        height: {
          xs: 'calc(100vh - 68px - 32px)',
          md: 'calc(100vh - 68px - 48px)',
        },
        '@supports (height: 100dvh)': {
          height: { xs: 'calc(100dvh - 68px - 32px)', md: 'calc(100dvh - 68px - 48px)' },
          maxHeight: { xs: 'calc(100dvh - 68px - 32px)', md: 'calc(100dvh - 68px - 48px)' },
        },
        maxHeight: { xs: 'calc(100vh - 68px - 32px)', md: 'calc(100vh - 68px - 48px)' },
        backgroundColor: 'background.paper',
        border: (t) => `1px solid ${t.palette.divider}`,
        borderRadius: '16px',
        overflow: 'hidden',
      }}
    >
      <AiChatConversation variant="page" />
    </Box>
  );
}
